import { Box, Button, IconButton, List, Tooltip, makeStyles } from '@material-ui/core';
import React, { useState, useEffect, useContext, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import AddIcon from '@material-ui/icons/Add';
import { ArrowBack } from '@mui/icons-material';
// Servicios
import {
  getNodes,
  getTags,
  postNode,
  updateNodesTree,
} from '../../services/hierarchy';
import { getStatusNodes } from '../../services/meters';
// Components
import AlertComponent from '../Elements/AlertComponent';
import Container from '../Elements/Container';
import LabelInfo from './LabelInfo';
import Labels from './Labels';
import NodeInfo from './NodeInfo';
import OrganizationTreeRow, { getOrganizationTreeRowHeight } from './OrganizationTreeRow';
import TopNav from '../common/TopNav';
import VirtualizedTreeList, { collectExpandableNodeIds } from '../common/VirtualizedTreeList';
import DragDropList from './DragDropList';
import StateLeftPanelSearch from '../Dashboards/State/StateLeftPanelSearch';
// Custom Hooks
import useDeviceDetect from '../../hooks/useDeviceDetect';
import useRemainingViewportHeight from '../../hooks/useRemainingViewportHeight';
// Context
import UserContext from '../../context/UserContext';
import NodesListContext from '../../context/NodesListContext';
import NodesContext from '../../context/NodesContext';
import TagsContext from '../../context/TagsContext';
import { useNodesTree } from '../../hooks/useNodesTree';
import { borderRadiusBoxes, stateDetailBodyStyle, stateDetailPanelStyle, stateDetailTitleStyle, stateTreeScrollAreaSx, themePalette } from '../Dashboards/State/stateTreeStyles';

const webkitScrollbar = {
  background: 'white',
  borderStyle: 'solid',
  borderWidth: '1px',
  borderColor: '#beb1b18f',
  width: '9px',
};

const webkitscrollbarTrack = {
  background: '#dfdfe957',
};

const useStyles = makeStyles(theme => ({
  root: {
    // margin: theme.spacing(2, 5, 2, 10),
    color: theme.palette.primary.main,
    display: 'flex',
    width: '100%',
    margin: 0,
    minHeight: 0,
  },
  btnTopNav: {
    backgroundColor: theme.palette.common.white,
    marginLeft: theme.spacing(2),
    minWidth: 0,
    '&:hover': {
      backgroundColor: theme.palette.common.white,
      opacity: 0.9,
      '& .MuiButton-label': {
        color: theme.palette.primary.main,
      },
    },
    '& .MuiButton-label': {
      fontSize: 11,
      textTransform: 'none',
    },
    '@media (max-width: 920px)': {
      marginLeft: 0,
    },
  },
  btnAddCompany: {
    marginBottom: '10px',
    '& .MuiButton-label': {
      fontSize: 11,
      textTransform: 'none',
    },
  },
  topNavTabs: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    width: '100%',
    '@media (max-width: 920px)': {
      justifyContent: 'flex-start',
    },
  },
  topNavTabButton: {
    marginLeft: '0 !important',
    '@media (max-width: 920px)': {
      flex: '1 1 132px',
    },
  },
  mobileAddCompanyBar: {
    display: 'flex',
    justifyContent: 'flex-start',
    padding: '0.25rem 0.75rem 0.35rem',
    marginTop: '0.15rem',
  },
  mobileAddCompanyButton: {
    borderRadius: borderRadiusBoxes,
    padding: '0.42rem 0.85rem',
    boxShadow: '0 10px 22px rgba(8, 95, 145, 0.16)',
    textTransform: 'none',
    fontSize: '0.8rem',
    fontWeight: 700,
    color: themePalette.primary.main,
    backgroundColor: theme.palette.common.white,
    marginLeft: '0 !important',
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(0.75),
    },
    '&:hover': {
      backgroundColor: themePalette.common.white,
      opacity: 0.94,
    },
  },
  mobileAddCompanyLabel: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  leftContainer: {
    width: '50%',
    paddingRight: 0,
    paddingLeft: 0,
    ...stateTreeScrollAreaSx,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    margin: 0,
    boxSizing: 'border-box',
    '&::-webkit-scrollbar': {
      //son los estilos de las barras scroll en una variable.
      ...webkitScrollbar,
    },
    '&::-webkit-scrollbar-track': {
      //son los estilos de las barras scroll en una variable.
      ...webkitscrollbarTrack,
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.primary.main,
    },
    '@media (max-width: 1220px)': {
      width: '100%',
      height: stateTreeScrollAreaSx.height,
      maxHeight: stateTreeScrollAreaSx.maxHeight,
    },
  },
  rightContainer: {
    ...stateDetailPanelStyle,
    width: '50%',
    margin: 0,
    borderLeft: `1px solid ${themePalette.border.main}`,
    '@media (max-width: 1220px)': {
      width: '100%',
      height: stateDetailPanelStyle.height,
      maxHeight: stateDetailPanelStyle.maxHeight,
      marginTop: theme.spacing(1.5),
      borderLeft: 'none',
      borderTop: `1px solid ${themePalette.border.main}`,
    },
  },
  mobileRightHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    borderBottom: `1px solid ${themePalette.border.main}`,
    background: `linear-gradient(180deg, ${themePalette.common.white} 0%, ${themePalette.surface.soft} 100%)`,
  },
  mobileRightTitle: {
    color: '#6b7280',
    fontSize: '0.9rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  titleContainer: {
    ...stateDetailTitleStyle,
  },
  title: {
    color: '#6b7280',
    fontSize: '0.9rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  rightBody: {
    ...stateDetailBodyStyle,
    '@media (max-width: 1220px)': {
      padding: '0.65rem 0.75rem',
    },
  },
  emptyState: {
    padding: theme.spacing(2),
    borderRadius: borderRadiusBoxes,
    border: `1px solid ${themePalette.border.soft}`,
    backgroundColor: themePalette.surface.soft,
    color: themePalette.text.disabled,
  },
}));

const areSetsEqual = (leftSet, rightSet) => {
  if (leftSet.size !== rightSet.size) return false;

  for (const value of leftSet) {
    if (!rightSet.has(value)) return false;
  }

  return true;
};

const ORGANIZATION_TREE_INDENT_SIZE = 20;
const initialOrganizationNodeFilterState = {
  hierarchy: [],
};
const ORGANIZATION_HIERARCHY_ORDER = ['empresa', 'zona', 'localización', 'sublocalización', 'extra', 'adicional'];
const ORGANIZATION_HIERARCHY_LABELS = {
  empresa: 'Empresa',
  zona: 'Zona',
  localización: 'Localización',
  sublocalización: 'Sublocalización',
  extra: 'Extra',
  adicional: 'Adicional',
};

const findNodeById = (nodes, targetId) => {
  for (const node of nodes || []) {
    if (node.id === targetId) return node;

    const childMatch = findNodeById(node.hijos || [], targetId);
    if (childMatch) return childMatch;
  }

  return null;
};

const normalizeOrganizationSearchText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const formatOrganizationFilterLabel = (value) =>
  String(value || '')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const collectOrganizationNodeSearchValues = (node) => [
  node?.id,
  node?.nombre,
  node?.descripcion,
  node?.level_nombre,
  node?.producto,
  node?.product,
  node?.medidor,
  node?.parent,
  node?.realtime?.llave,
  ...(node?.tags || []).flatMap((tag) => [tag?.id, tag?.nombre]),
  ...(node?.automates || []).flatMap((automate) => [
    automate?.id,
    automate?.nombre,
    automate?.llave,
    automate?.producto,
  ]),
];

const buildOrganizationNodeSearchIndex = (node) =>
  normalizeOrganizationSearchText(
    collectOrganizationNodeSearchValues(node)
      .filter(Boolean)
      .join(' ')
  );

const getOrganizationNodeHierarchyFilterValue = (node) =>
  normalizeOrganizationSearchText(node?.level_nombre);

const buildOrganizationFilterSections = (tree) => {
  const hierarchyValues = new Set();

  const walkTree = (nodes) => {
    (nodes || []).forEach((node) => {
      const hierarchyValue = getOrganizationNodeHierarchyFilterValue(node);
      if (hierarchyValue) hierarchyValues.add(hierarchyValue);
      walkTree(node?.hijos);
    });
  };

  walkTree(tree);

  return [
    {
      key: 'hierarchy',
      title: 'Jerarqui­a',
      options: Array.from(hierarchyValues)
        .sort((leftValue, rightValue) => {
          const leftIndex = ORGANIZATION_HIERARCHY_ORDER.indexOf(leftValue);
          const rightIndex = ORGANIZATION_HIERARCHY_ORDER.indexOf(rightValue);

          if (leftIndex === -1 && rightIndex === -1) return leftValue.localeCompare(rightValue);
          if (leftIndex === -1) return 1;
          if (rightIndex === -1) return -1;
          return leftIndex - rightIndex;
        })
        .map((value) => ({
          value,
          label: ORGANIZATION_HIERARCHY_LABELS[value] || formatOrganizationFilterLabel(value),
        })),
    },
  ];
};

const filterOrganizationTreeBySearch = ({
  nodes,
  rawSearchTerm,
  selectedFilters = initialOrganizationNodeFilterState,
}) => {
  const normalizedSearchTerm = normalizeOrganizationSearchText(rawSearchTerm);
  const hasActiveFilters = Object.values(selectedFilters).some((values) => values.length > 0);

  if (!normalizedSearchTerm && !hasActiveFilters) {
    return {
      filteredTree: nodes || [],
      expandedSearchNodeIds: new Set(),
      hasMatches: true,
    };
  }

  const expandedSearchNodeIds = new Set();

  const filterNode = (node) => {
    const filteredChildren = (node?.hijos || [])
      .map(filterNode)
      .filter(Boolean);

    const hierarchyValue = getOrganizationNodeHierarchyFilterValue(node);
    const searchMatches = !normalizedSearchTerm || buildOrganizationNodeSearchIndex(node).includes(normalizedSearchTerm);
    const hierarchyMatches =
      selectedFilters.hierarchy.length === 0 ||
      (hierarchyValue && selectedFilters.hierarchy.includes(hierarchyValue));
    const nodeMatches = searchMatches && hierarchyMatches;

    if (!nodeMatches && filteredChildren.length === 0) return null;

    if (filteredChildren.length > 0 && node?.id !== undefined) {
      expandedSearchNodeIds.add(node.id);
    }

    return {
      ...node,
      hijos: filteredChildren,
    };
  };

  const filteredTree = (nodes || [])
    .map(filterNode)
    .filter(Boolean);

  return {
    filteredTree,
    expandedSearchNodeIds,
    hasMatches: filteredTree.length > 0,
  };
};

const reorderChildrenByPositionMap = (children, positionChildren = []) => {
  if (!Array.isArray(children) || children.length === 0) return children || [];
  if (!Array.isArray(positionChildren) || positionChildren.length === 0) return children;

  const nextChildren = [];

  positionChildren.forEach((sourceIndex, targetIndex) => {
    const normalizedSourceIndex = parseInt(sourceIndex, 10);
    if (!Number.isInteger(normalizedSourceIndex)) return;

    nextChildren[targetIndex] = children[normalizedSourceIndex];
  });

  return nextChildren.filter(Boolean);
};

const reorderTreeNodeChildren = (nodes, targetId, positionChildren = []) =>
  (nodes || []).map((node) => {
    if (node.id === targetId) {
      return {
        ...node,
        hijos: reorderChildrenByPositionMap(node.hijos, positionChildren),
      };
    }

    if (!node.hijos?.length) return node;

    return {
      ...node,
      hijos: reorderTreeNodeChildren(node.hijos, targetId, positionChildren),
    };
  });

const flattenTreeNodes = (nodes) =>
  (nodes || []).reduce((accumulator, node) => {
    accumulator.push(node);

    if (node.hijos?.length) {
      accumulator.push(...flattenTreeNodes(node.hijos));
    }

    return accumulator;
  }, []);

const appendNodeToParent = (nodes, parentId, nextNode, insertAtStart = false) =>
  (nodes || []).map((node) => {
    if (node.id === parentId) {
      return {
        ...node,
        hijos: insertAtStart ? [nextNode, ...(node.hijos || [])] : [...(node.hijos || []), nextNode],
      };
    }

    if (!node.hijos?.length) return node;

    return {
      ...node,
      hijos: appendNodeToParent(node.hijos, parentId, nextNode, insertAtStart),
    };
  });

const updateTreeNodeById = (nodes, targetId, updater) =>
  (nodes || []).map((node) => {
    if (node.id === targetId) {
      return updater(node);
    }

    if (!node.hijos?.length) return node;

    return {
      ...node,
      hijos: updateTreeNodeById(node.hijos, targetId, updater),
    };
  });

const removeTreeNodeById = (nodes, targetId) =>
  (nodes || [])
    .filter((node) => node.id !== targetId)
    .map((node) => ({
      ...node,
      hijos: removeTreeNodeById(node.hijos || [], targetId),
    }));

export default function OrganizationSettings({ match }) {
  const classes = useStyles();
  const { isMobile } = useDeviceDetect(1200);
  const { plan } = useContext(UserContext);
  const isDemoPlan = plan?.info?.plan === 'Demo';
  const isFreePlan = plan?.info?.plan === 'Gratuito';
  const { setNodes } = useContext(NodesContext);
  const { tags, setTags } = useContext(TagsContext);
  const { tree, replaceTree, loading: treeLoading } = useNodesTree();
  const [statusLoading, setStatusLoading] = useState(true);
  const { nodesList, setNodesList } = useContext(NodesListContext);
  const [status, setStatus] = useState([])
  const [selectedNode, setSelectedNode] = useState({});
  const [selectedLabel, setSelectedLabel] = useState('');
  const [treeChanges, setTreeChanges] = useState(false);
  const [treeLabelsChanges, setTreeLabelsChanges] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilters, setSelectedFilters] = useState(initialOrganizationNodeFilterState);
  const [filtersAnchorEl, setFiltersAnchorEl] = useState(null);

  const [showRightBlock, setShowRightBlock] = useState(true);
  const [showLeftBlock, setShowLeftBlock] = useState(true);
  //Estados alert
  const [close, setClose] = useState(false);
  const [msg, setMsg] = useState({
    msg: '',
    type: 'success',
  });
  const [flagEdit, setFlagEdit] = useState({})
  const [newChangesToPositions, setNewChangesToPositions] = useState(null)
  const [expandedNodeIds, setExpandedNodeIds] = useState(() => new Set());
  const hasInitializedExpandedNodes = useRef(false);
  const finalizeEditExitFrameRef = useRef(null);
  const leftPanelScrollRef = useRef(null);
  const virtualizedTreeScrollRef = useRef(null);
  const pendingLeftPanelScrollTopRef = useRef(null);
  const topNavRef = useRef(null);
  const { remainingHeight: contentViewportHeight } = useRemainingViewportHeight(topNavRef);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const resStatus = await getStatusNodes();
        setStatus(resStatus.data);
        setStatusLoading(false);
      }
      catch (error) {
        setStatusLoading(false);
        console.error(error);
      }
    };
    fetchStatus();
  }, [treeChanges]);

  const loading = treeLoading || statusLoading;

  useEffect(() => {
    if (isMobile) {
      setShowRightBlock(false);
    }
    else {
      setShowLeftBlock(true);
      setShowRightBlock(true);
    }
  }, [isMobile]);

  useEffect(() => {
    if (newChangesToPositions !== null) {
      const currentNodeToEdit = findNodeById(tree, flagEdit?.node_id);
      const reorderedChildren = reorganizeChildrens(currentNodeToEdit?.hijos || [], flagEdit?.position_children || []);
      const reorderedTree = reorderTreeNodeChildren(tree, flagEdit?.node_id, flagEdit?.position_children);
      const reorderedSelectedNode = findNodeToEditChildrenPositions(reorderedTree, flagEdit?.node_id);

      replaceTree(reorderedTree);
      setNodes(reorderedTree);

      if (reorderedSelectedNode) {
        setSelectedNode({
          ...reorderedSelectedNode,
          hijos: reorderedChildren,
        });
      }

      setFlagEdit({});
      setNewChangesToPositions(null);
      saveNewOrder(reorderedTree)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newChangesToPositions]);

  useEffect(() => {
    const fetchLabelsData = async () => {
      const res = await getTags();
      setTags(res.data);
    };

    fetchLabelsData();
  }, [treeLabelsChanges, setTags]);

  useEffect(() => {
    const flattenedNodes = flattenTreeNodes(tree);

    setNodes(tree);
    setNodesList(flattenedNodes);
  }, [setNodes, setNodesList, tree]);

  const [showNodes, setShowNodes] = useState(true);
  const expandableNodeIds = useMemo(() => collectExpandableNodeIds(tree), [tree]);
  const isEditingTree = Boolean(flagEdit?.node_id);
  const editingNode = useMemo(
    () => (isEditingTree ? findNodeById(tree, flagEdit?.node_id) : null),
    [flagEdit?.node_id, isEditingTree, tree]
  );
  const editingNodeChildren = useMemo(
    () => editingNode?.hijos || [],
    [editingNode]
  );
  const visibleExpandedNodeIds = useMemo(() => {
    if (!isEditingTree) return expandedNodeIds;

    const nextExpandedNodeIds = new Set(expandedNodeIds);
    nextExpandedNodeIds.delete(flagEdit?.node_id);
    return nextExpandedNodeIds;
  }, [expandedNodeIds, flagEdit?.node_id, isEditingTree]);
  const treeToRender = useMemo(
    () => tree,
    [tree]
  );
  const normalizedSearchTerm = useMemo(() => normalizeOrganizationSearchText(searchTerm), [searchTerm]);
  const activeFilterCount = useMemo(
    () => Object.values(selectedFilters).reduce((total, values) => total + values.length, 0),
    [selectedFilters]
  );
  const filterSections = useMemo(() => buildOrganizationFilterSections(tree), [tree]);
  const {
    filteredTree,
    expandedSearchNodeIds,
    hasMatches: hasSearchMatches,
  } = useMemo(
    () =>
      filterOrganizationTreeBySearch({
        nodes: treeToRender,
        rawSearchTerm: normalizedSearchTerm,
        selectedFilters,
      }),
    [normalizedSearchTerm, selectedFilters, treeToRender]
  );
  const isFilteringTree = Boolean(normalizedSearchTerm) || activeFilterCount > 0;
  const visibleTree = useMemo(
    () => (isFilteringTree ? filteredTree : treeToRender),
    [filteredTree, isFilteringTree, treeToRender]
  );
  const effectiveExpandedNodeIds = useMemo(() => {
    if (!isFilteringTree) return visibleExpandedNodeIds;

    const nextExpandedNodeIds = new Set([
      ...visibleExpandedNodeIds,
      ...expandedSearchNodeIds,
    ]);

    if (isEditingTree) {
      nextExpandedNodeIds.delete(flagEdit?.node_id);
    }

    return nextExpandedNodeIds;
  }, [expandedSearchNodeIds, flagEdit?.node_id, isEditingTree, isFilteringTree, visibleExpandedNodeIds]);
  const filterPopoverPaperStyle = useMemo(
    () =>
      isMobile
        ? {
          width: 'auto',
          height: 'auto',
          maxWidth: 'none',
          maxHeight: 'none',
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          marginTop: 0,
          borderRadius: 0,
          border: 'none',
          boxShadow: themePalette.shadow.dialogStrong,
          overflow: 'hidden',
          zIndex: 2001,
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }
        : {
          width: '340px',
          maxHeight: '620px',
          marginTop: '0.2rem',
          borderRadius: '0.3rem',
          border: `1px solid ${themePalette.border.main}`,
          boxShadow: themePalette.shadow.dialog,
          overflow: 'hidden',
        },
    [isMobile]
  );

  useLayoutEffect(() => {
    if (pendingLeftPanelScrollTopRef.current === null) return undefined;

    let timeoutId = null;

    const restoreScrollPosition = () => {
      if (!leftPanelScrollRef.current) return;

      leftPanelScrollRef.current.scrollTop = pendingLeftPanelScrollTopRef.current;
      timeoutId = window.setTimeout(() => {
        if (!leftPanelScrollRef.current) return;

        leftPanelScrollRef.current.scrollTop = pendingLeftPanelScrollTopRef.current;
        pendingLeftPanelScrollTopRef.current = null;
      }, 0);
    };

    const frameId = window.requestAnimationFrame(restoreScrollPosition);
    return () => {
      window.cancelAnimationFrame(frameId);
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [isEditingTree]);

  useEffect(() => {
    //Si recibo dispositivos/1 muestro Nodos sino muestro etiqeutas
    if (match.path === '/configuracion-organizacion') {
      setShowNodes(true);
    } else {
      setShowNodes(false);
    }
  }, [match.path]);

  useEffect(() => {
    if (!hasInitializedExpandedNodes.current) {
      if (tree.length === 0) {
        return;
      }
      hasInitializedExpandedNodes.current = true;
      setExpandedNodeIds(new Set(expandableNodeIds));
      return;
    }

    setExpandedNodeIds((previousExpandedNodes) => {
      const nextExpandedNodes = new Set();

      expandableNodeIds.forEach((nodeId) => {
        if (previousExpandedNodes.has(nodeId)) {
          nextExpandedNodes.add(nodeId);
        }
      });

      return areSetsEqual(previousExpandedNodes, nextExpandedNodes) ? previousExpandedNodes : nextExpandedNodes;
    });
  }, [expandableNodeIds, tree.length]);

  const handleSelectedNode = useCallback((node) => {
    setSelectedNode(node);
    if (isMobile) {
      setShowLeftBlock(false);
      setShowRightBlock(true);
    }
  }, [isMobile]);

  const handleToggleExpandedNode = useCallback((nodeId) => {
    setExpandedNodeIds((previousExpandedNodes) => {
      const nextExpandedNodes = new Set(previousExpandedNodes);

      if (nextExpandedNodes.has(nodeId)) {
        nextExpandedNodes.delete(nodeId);
      } else {
        nextExpandedNodes.add(nodeId);
      }

      return nextExpandedNodes;
    });
  }, []);

  const handleStartEditNode = useCallback((node) => {
    pendingLeftPanelScrollTopRef.current = virtualizedTreeScrollRef.current?.scrollTop ?? leftPanelScrollRef.current?.scrollTop ?? 0;
    setFlagEdit({
      node_id: node.id,
      node_name: node.nombre,
      node_level_name: node.level_nombre,
    });
  }, []);

  const handleSaveEditOrder = useCallback(() => {
    setNewChangesToPositions({});
  }, []);

  const closeEditMode = useCallback(() => {
    setNewChangesToPositions(null);

    if (finalizeEditExitFrameRef.current !== null) {
      window.cancelAnimationFrame(finalizeEditExitFrameRef.current);
    }

    finalizeEditExitFrameRef.current = window.requestAnimationFrame(() => {
      setFlagEdit({});
      finalizeEditExitFrameRef.current = null;
    });
  }, []);

  const handleCancelEditOrder = useCallback(() => {
    closeEditMode();
  }, [closeEditMode]);

  useEffect(() => () => {
    if (finalizeEditExitFrameRef.current !== null) {
      window.cancelAnimationFrame(finalizeEditExitFrameRef.current);
    }
  }, []);

  const saveNewOrder = async (nextTree) => {
    try {
      await updateNodesTree(nextTree)
    } catch (error) {
      setClose(true);
      setMsg({
        msg: `${`Ocurrio un error inesperado`}.`,
        type: 'error',
      });
      setTreeChanges(current => !current);
    }
  }

  const handleLocalNodeUpdate = useCallback((updatedNode) => {
    const nextTree = updateTreeNodeById(tree, updatedNode.id, (currentNode) => ({
      ...currentNode,
      ...updatedNode,
      hijos: updatedNode.hijos ?? currentNode.hijos,
    }));

    replaceTree(nextTree);
    setSelectedNode((currentSelectedNode) =>
      currentSelectedNode?.id === updatedNode.id
        ? {
          ...currentSelectedNode,
          ...updatedNode,
          hijos: updatedNode.hijos ?? currentSelectedNode.hijos,
        }
        : currentSelectedNode
    );
  }, [replaceTree, tree]);

  const handleLocalNodeDelete = useCallback((deletedNodeId) => {
    const nextTree = removeTreeNodeById(tree, deletedNodeId);

    replaceTree(nextTree);
    setSelectedNode((currentSelectedNode) =>
      currentSelectedNode?.id === deletedNodeId ? {} : currentSelectedNode
    );
  }, [replaceTree, tree]);

  const handleSelectedLabel = (label, id, color) => {
    setSelectedLabel({ nombre: label, id: id, color: color });
    if (isMobile) {
      setShowLeftBlock(false);
      setShowRightBlock(true);
    }
  };

  const handleOpenFilters = useCallback((event) => {
    setFiltersAnchorEl(event.currentTarget);
  }, []);

  const handleCloseFilters = useCallback(() => {
    setFiltersAnchorEl(null);
  }, []);

  const toggleFilterValue = useCallback((filterKey, nextValue) => {
    setSelectedFilters((currentFilters) => {
      const currentValues = currentFilters[filterKey] || [];
      const nextValues = currentValues.includes(nextValue)
        ? currentValues.filter((value) => value !== nextValue)
        : [...currentValues, nextValue];

      return {
        ...currentFilters,
        [filterKey]: nextValues,
      };
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setSelectedFilters(initialOrganizationNodeFilterState);
  }, []);

  const handleAddCompany = async () => {
    try {
      let treeAux = tree
      if (tree.length === 0) {
        treeAux = nodesList
      }

      const firstTopLevelNode = treeAux[0] || treeAux.find(node => node?.level_nombre === 'empresa');
      let parent = firstTopLevelNode?.parent;

      if (!parent) {
        const nodesResponse = await getNodes();
        const allNodes = nodesResponse?.data || [];
        const rootNode = allNodes.find(node => node?.level_nombre === 'raiz' || node?.parent === null);
        const firstCompanyNode = allNodes.find(node => node?.level_nombre === 'empresa');
        parent = firstCompanyNode?.parent ?? rootNode?.id;
      }

      if (!parent) {
        throw new Error('No se pudo determinar el parent para crear una nueva empresa.');
      }
      const res = await postNode({
        parent: parent,
        nombre: `Nueva Empresa`,
        descripcion: '',
        medidor_virtual: true,
        medidor: null,
      });
      const createdCompanyNode = {
        ...res.data,
        owner: true,
      };
      replaceTree([createdCompanyNode, ...(tree || [])]);
      //Selecciono la empresa creada en este momento
      handleSelectedNode(createdCompanyNode);
      setClose(true);
      setMsg({
        msg: 'Se creo una nueva empresa',
        type: 'success',
      });
      setTreeChanges(current => !current);
    }
    catch (error) {
      if (error.response) {
        setClose(true);
        setMsg({
          msg: `${error.response.data.msg}.`,
          type: 'error',
        });
      }
      else {
        setClose(true);
        setMsg({
          msg: `${`Ocurrio un error inesperado`}.`,
          type: 'error',
        });
      }
    }
  };

  const alertNodo = () => {
    //alert
    setClose(true);
    setMsg({
      msg: 'El nodo se elimino correctamente',
      type: 'success',
    });
  };

  const handleBackClick = () => {
    setShowLeftBlock(true);
    setShowRightBlock(false);
    closeEditMode();
  };

  const getTotalOfNodes = useCallback(type => {
    return tree.filter(node => node?.level_nombre === type).length;
  }, [tree]);
  const isAddCompanyLimitReached = getTotalOfNodes('empresa') + 1 > plan?.menu?.max_locations;

  const findNodeToEditChildrenPositions = (nodes, id) => {
    for (const node of nodes) {
      if (node.id === id) {
        return node;
      }
      if (node.hijos && node.hijos.length > 0) {
        let childrenNode = findNodeToEditChildrenPositions(node.hijos, id);
        if (childrenNode) {
          return childrenNode;
        }
      }
    }
    return null; // Devuelve null si no se encuentra ningun objeto con el ID especificado
  }

  const reorganizeChildrens = (array, positions) => {
    const reorganizedChildrens = [];
    Object.keys(positions).forEach(key => {
      const index = parseInt(key);
      const newPosition = positions[key];

      reorganizedChildrens[newPosition] = array[index];
    });
    return reorganizedChildrens;
  }

  const getTotalOfChildNodes = useCallback(type => {
    switch (type) {
      case 'empresa':
        return nodesList.filter(node => node?.level_nombre === 'zona').length;
      case 'zona':
        return nodesList.filter(node => node?.level_nombre === 'localización').length;
      case 'localización':
        return nodesList.filter(node => node?.level_nombre === 'sublocalización').length;
      case 'sublocalización':
        return nodesList.filter(node => node?.level_nombre === 'extra').length;
      case 'extra':
        return nodesList.filter(node => node?.level_nombre === 'adicional').length;
      case 'adicional':
      default:
        return 0;
    }
  }, [nodesList]);

  const getPluralOfChild = useCallback(type => {
    switch (type) {
      case 'empresa':
        return 'zonas';
      case 'zona':
        return 'localizaciones';
      case 'localización':
        return 'sublocalizaciones';
      case 'sublocalización':
        return 'extras';
      case 'extra':
        return 'adicionales';
      case 'adicional':
        return 'adicionales';
      default:
        return 'extras';
    }
  }, []);

  const getSingularOfChild = useCallback(type => {
    switch (type) {
      case 'empresa':
        return 'zona';
      case 'zona':
        return 'localizacion';
      case 'localizacion':
        return 'sublocalizacion';
      case 'sublocalizacion':
        return 'extra';
      case 'extra':
        return 'adicional';
      case 'adicional':
        return 'adicional';
      default:
        return 'extra';
    }
  }, []);

  const totalLocationsCount = useMemo(
    () => nodesList.filter((node) => node?.level_nombre === 'localización').length,
    [nodesList]
  );

  const freePlanLocationCount = useMemo(
    () => nodesList.filter((node) => normalizeOrganizationSearchText(node?.level_nombre) === 'localización').length,
    [nodesList]
  );
  void totalLocationsCount;
  void getTotalOfChildNodes;
  void getPluralOfChild;
  void getSingularOfChild;

  const handleAddNode = useCallback(async (node) => {
    const family = [
      { self: 'raiz', child: 'Empresa' },
      { self: 'empresa', child: 'Zona' },
      { self: 'zona', child: 'Localización' },
      { self: 'localización', child: 'Sublocalización' },
      { self: 'sublocalización', child: 'Extra' },
      { self: 'extra', child: 'Adicional' },
    ];
    const childNodeName = family.find(member => member.self === node.level_nombre)?.child || 'Nodo';

    try {
      if (isFreePlan && node?.level_nombre === 'zona' && freePlanLocationCount >= 1) {
        setClose(true);
        setMsg({
          msg: 'El plan Gratuito solo permite una localización.',
          type: 'error',
        });
        return;
      }

      const res = await postNode({
        parent: node.id,
        nombre: `Nueva ${childNodeName}`,
        descripcion: '',
        medidor_virtual: true,
        medidor: null,
      });
      const createdNode = {
        ...res.data,
        owner: true,
      };
      replaceTree(appendNodeToParent(tree, node.id, createdNode, true));
      setExpandedNodeIds((previousExpandedNodes) => {
        const nextExpandedNodes = new Set(previousExpandedNodes);
        nextExpandedNodes.add(node.id);
        return nextExpandedNodes;
      });
      setClose(true);
      setMsg({
        msg: 'Nodo agregado correctamente',
        type: 'success',
      });
      setTreeChanges(current => !current);
    } catch (error) {
      console.error(error);
      setClose(true);
      setMsg({
        msg: `${`Ocurrio un error inesperado`}.`,
        type: 'error',
      });
    }
  }, [freePlanLocationCount, isFreePlan, replaceTree, setClose, tree]);

  const organizationTreeOverscanPx = isMobile ? 220 : 420;

  const renderOrganizationTreeRow = useCallback((item) => {
    const isEditingRow = isEditingTree && item.node.id === flagEdit?.node_id;
    const editingChildrenCount = editingNodeChildren.length;

    return (
      <Box>
        <OrganizationTreeRow
          item={item}
          selectedNodeId={selectedNode?.id}
          onSelectNode={handleSelectedNode}
          onToggleExpanded={handleToggleExpandedNode}
          onStartEdit={handleStartEditNode}
          onSaveEditOrder={handleSaveEditOrder}
          onCancelEditOrder={handleCancelEditOrder}
          onAddNode={handleAddNode}
          nodeStatus={status?.find(n => n.node_id === item.node.id)}
          isDemoPlan={isDemoPlan}
          isFreePlan={isFreePlan}
          isMobile={isMobile}
          totalLocationsCount={freePlanLocationCount}
          isEditingNode={isEditingRow}
          editableChildrenCount={editingChildrenCount}
        />

        {isEditingRow && editingChildrenCount > 0 && (
          <Box
            style={{
              width: '100%',
              paddingLeft: `${(item.depth + 1) * ORGANIZATION_TREE_INDENT_SIZE}px`,
              paddingTop: 4,
              boxSizing: 'border-box',
            }}
          >
            <DragDropList
              setClose={setClose}
              setFlagEdit={setFlagEdit}
              flagEdit={flagEdit}
              array={editingNodeChildren}
              handleSelectedNode={handleSelectedNode}
              selectedNode={selectedNode}
              setTreeChanges={setTreeChanges}
              setNewChangesToPositions={setNewChangesToPositions}
            />
          </Box>
        )}
      </Box>
    );
  }, [
    editingNodeChildren,
    flagEdit,
    handleAddNode,
    handleCancelEditOrder,
    handleSaveEditOrder,
    handleSelectedNode,
    handleStartEditNode,
    handleToggleExpandedNode,
    isDemoPlan,
    isFreePlan,
    isEditingTree,
    isMobile,
    selectedNode,
    setClose,
    status,
    freePlanLocationCount,
  ]);

  const getVirtualizedRowHeight = useCallback((item) => {
    const baseHeight = getOrganizationTreeRowHeight({ isCompact: isMobile });
    if (!isEditingTree || item.node.id !== flagEdit?.node_id) return baseHeight;

    return baseHeight + (editingNodeChildren.length * 56) + 12;
  }, [editingNodeChildren.length, flagEdit?.node_id, isEditingTree, isMobile]);

  const contentAreaStyle = contentViewportHeight !== null
    ? {
      height: `${contentViewportHeight}px`,
      maxHeight: `${contentViewportHeight}px`,
      overflow: 'hidden',
    }
    : { overflow: 'hidden' };

  const leftPanelStyle = {
    height: '100%',
    maxHeight: '100%',
    ...(showNodes
      ? {
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }
      : {}),
  };

  const rightPanelStyle = {
    height: '100%',
    maxHeight: '100%',
    minHeight: 0,
    marginTop: isMobile ? 0 : undefined,
  };

  return (
    <>
      <Box ref={topNavRef}>
        <TopNav
          titulo={showNodes ? 'Organizacion' : 'Etiquetas'}
          titleActions={
            showNodes && plan?.info?.plan !== 'Demo' && !isMobile ? (
              <Tooltip
                title={isAddCompanyLimitReached
                  ? 'Ha alcanzado el maximo de empresas permitidas para su plan.'
                  : 'Presione para crear una empresa'}
                arrow
                placement='right'
              >
                <span>
                  <Button
                    variant="contained"
                    color="default"
                    className={classes.btnTopNav}
                    onClick={handleAddCompany}
                    disabled={isAddCompanyLimitReached}
                  >
                    Nueva Empresa
                  </Button>
                </span>
              </Tooltip>
            ) : null
          }
        >
          <Box className={classes.topNavTabs}>
            <Button
              color="default"
              className={`${classes.btnTopNav} ${classes.topNavTabButton}`}
              onClick={() => {
                setShowNodes(true);
                setSelectedLabel(false);
              }}
            >
              Configuracion del nodo
            </Button>
            <Button
              color="default"
              className={`${classes.btnTopNav} ${classes.topNavTabButton}`}
              onClick={() => {
                setShowNodes(false);
                setSelectedNode({});
              }}
            >
              Etiquetas
            </Button>
          </Box>
        </TopNav>
      </Box>

      {/* ALERTAS */}
      <AlertComponent
        severity={msg.type}
        msg={msg.msg}
        setClose={setClose}
        close={close}
        alertStyle={{ zIndex: 3001 }}
      />

      <Container>
        {loading && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress />
          </Box>
        )}
        {!loading && (
          <Box className={classes.root} style={contentAreaStyle}>
            {showLeftBlock && (
              <Box
                ref={leftPanelScrollRef}
                className={classes.leftContainer}
                style={leftPanelStyle}
              >
                <List
                  disablePadding
                  style={showNodes ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } : undefined}
                >
                  {showNodes && (
                    <Box
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        minHeight: 0,
                      }}
                    >
                      {false && (
                        <Tooltip title={getTotalOfNodes('empresa') + 1 > plan?.menu?.max_locations ? 'Ha alcanzado el maximo de empresas permitidas para su plan.' : 'Presione para crear una empresa'} arrow placement='right'>
                          <span>
                            <Button
                              variant="contained"
                              color="primary"
                              className={classes.btnAddCompany}
                              onClick={handleAddCompany}
                              // Deshabilito el boton si en caso de crear (por eso el +1) superar­a la cantidad admitida.
                              disabled={getTotalOfNodes('empresa') + 1 > plan?.menu?.max_locations}
                            >
                              Nueva Empresa
                            </Button>
                          </span>

                        </Tooltip>
                      )}
                      <StateLeftPanelSearch
                        isMobile={isMobile}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        activeFilterCount={activeFilterCount}
                        handleOpenFilters={handleOpenFilters}
                        filtersAnchorEl={filtersAnchorEl}
                        handleCloseFilters={handleCloseFilters}
                        filterPopoverPaperStyle={filterPopoverPaperStyle}
                        filterSections={filterSections}
                        selectedFilters={selectedFilters}
                        toggleFilterValue={toggleFilterValue}
                        clearAllFilters={clearAllFilters}
                      />
                      {isMobile && showNodes && !isDemoPlan && (
                        <Box className={classes.mobileAddCompanyBar}>
                          <Tooltip
                            title={isAddCompanyLimitReached
                              ? 'Ha alcanzado el maximo de empresas permitidas para su plan.'
                              : 'Presione para crear una empresa'}
                            arrow
                            placement='bottom'
                          >
                            <span>
                              <Button
                                variant="contained"
                                color="default"
                                className={classes.mobileAddCompanyButton}
                                onClick={handleAddCompany}
                                disabled={isAddCompanyLimitReached}
                                startIcon={<AddIcon fontSize='small' />}
                              >
                                <span className={classes.mobileAddCompanyLabel}>Nueva Empresa</span>
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      )}
                      <VirtualizedTreeList
                        tree={visibleTree}
                        expandedIds={effectiveExpandedNodeIds}
                        renderRow={renderOrganizationTreeRow}
                        getRowHeight={getVirtualizedRowHeight}
                        scrollRef={virtualizedTreeScrollRef}
                        virtualization={{
                          listPaddingLeft: 12,
                          listPaddingRight: 0,
                          rowGap: 4,
                          overscanPx: organizationTreeOverscanPx,
                        }}
                        layout={{
                          containerStyle: {
                            flex: 1,
                            minHeight: 0,
                          },
                          contentStyle: {
                            margin: 0,
                          },
                        }}
                        emptyState={(
                          <Box className={classes.emptyState} m={2}>
                            {isFilteringTree && !hasSearchMatches
                              ? 'No encontramos nodos con ese criterio de busqueda.'
                              : 'No hay nodos disponibles para mostrar.'}
                          </Box>
                        )}
                      />
                    </Box>
                  )}
                  {!showNodes && (
                    <Labels
                      handleSelectedLabel={handleSelectedLabel}
                      selectedLabel={selectedLabel}
                      treeLabelsChanges={treeLabelsChanges}
                      setTreeLabelsChanges={setTreeLabelsChanges}
                      fullTagList={tags}
                    />
                  )}
                </List>
              </Box>
            )}
            {showRightBlock && (
              <Box className={classes.rightContainer} style={rightPanelStyle}>
                {showNodes && !selectedNode.level_nombre && (
                  <>
                    <Box className={classes.titleContainer}>
                      <Box className={classes.title}>Configuracion del nodo</Box>
                    </Box>
                    <Box className={classes.rightBody}>
                      <Box className={classes.emptyState}>
                        Selecciona un nodo del arbol para ver sus datos y configuracion.
                      </Box>
                    </Box>
                  </>
                )}
                {showNodes && selectedNode.level_nombre && (
                  <>
                    {isMobile && (
                      <Box className={classes.mobileRightHeader}>
                        <IconButton onClick={handleBackClick} style={{ color: themePalette.primary.main }}>
                          <ArrowBack fontSize="small" />
                        </IconButton>
                        <Box className={classes.mobileRightTitle}>Configuracion del nodo</Box>
                      </Box>
                    )}
                    <NodeInfo
                      handleSelectedNode={handleSelectedNode}
                      onNodeDelete={handleLocalNodeDelete}
                      onNodeUpdate={handleLocalNodeUpdate}
                      setMsg={setMsg}
                      setClose={setClose}
                      alertNodo={alertNodo}
                      selectedNode={selectedNode}
                      setSelectedNode={(selectedNode) => {
                        setSelectedNode(selectedNode);
                        isMobile && handleBackClick();
                      }}
                      setTreeChanges={setTreeChanges}
                      treeChanges={treeChanges}
                      nodes={nodesList}
                      hidePanelHeader={isMobile}
                    />
                  </>
                )}
                {!showNodes && selectedLabel && (
                  <LabelInfo
                    plan={plan}
                    selectedLabel={selectedLabel}
                    setSelectedLabel={setSelectedLabel}
                    treeLabelsChanges={treeLabelsChanges}
                    setTreeLabelsChanges={setTreeLabelsChanges}
                  />
                )}
              </Box>
            )}
          </Box>
        )}
      </Container>
    </>
  );
}



